import React, { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { formatDateTime } from '../../utils/Utils';
import {
  useOneOnOnesApi, Badge, ONE_ON_ONE_STATUS_COLORS, ONE_ON_ONE_ROLE_COLORS,
} from './config';
import NewMeetingModal from './components/NewMeetingModal';
import OneOnOneDetailView from './components/OneOnOneDetailView';
import DataTable from '../../components/ui/DataTable';
import Button from '../../components/ui/Button';

const Container = styled.div``;

const EmptyManagerCard = styled.div`
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 10px;
  padding: 40px 24px;
  text-align: center;
`;

const EmptyManagerText = styled.p`
  color: #6c757d;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  margin-bottom: 16px;
`;

// Faz D (spec 2026-08-08 §4, Task 4) — 1:1 Görüşmelerim: liste+detay TEK
// SAYFA, ayrı bir /:id rotası YOK (comp-cycles'ın İK sekmesiyle AYNI
// mimari — desen aynası, task-4-brief.md). GET v1/one-on-ones/my TEK kaynak
// (task-1-report.md §1) — yönetici+çalışan satırları TEK listede, `myRole`
// ile ayrışır.
//
// Fix round 1 (coordinator review) — "Yeni Görüşme" butonu artık BE'nin
// `canCreate` alanına göre render edilir (meeting listesinin doluluğundan
// TAMAMEN BAĞIMSIZ — eski isManager = data.some(myRole==='manager') sezgisi,
// hiç 1:1'i olmayan bir yöneticinin İLK görüşmesini asla oluşturamaması
// bulgusuyla TERK EDİLDİ, bkz. task-4-report.md "Fix round 1"). Liste boş VE
// canCreate=true ise DataTable yerine oluşturmaya davet eden bir boş-durum
// kartı gösterilir; canCreate=false ise (saf çalışan) DataTable'ın kendi
// varsayılan boş-durumu (common.noRecords) değişmeden kalır.
// PM Analitik Faz 2 (E18 "Performansım" > Görüşmelerim sekmesi) — gömülü kullanımda sekme kabının tek kaydırması için DataTable
// gövde yüksekliği ('none' = iç kaydırma yok). Rota olarak açıldığında verilmez; davranış aynen eskisidir.
export default function OneOnOnesPage({ tableBodyMaxHeight } = {}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const {
    data, canCreate, fetching, createMeeting, updateMeeting, addItem, updateItem, deleteItem,
  } = useOneOnOnesApi();

  const showEmptyManagerHint = !fetching && data.length === 0 && canCreate;

  const [selectedId, setSelectedId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [carryOverInfo, setCarryOverInfo] = useState(null);

  const selectedMeeting = useMemo(
    () => data.find((m) => m.id === selectedId) || null,
    [data, selectedId],
  );

  const openDetail = useCallback((row) => {
    setSelectedId(row.id);
    setCarryOverInfo(null);
  }, []);

  const backToList = useCallback(() => setSelectedId(null), []);

  // Create yanıtındaki items her zaman carry-over'dır (config.createMeeting
  // yorumu) — burada sayılır ve YALNIZ bu meeting seçiliyken bandı tetikler
  // (openDetail'de sıfırlanır: listeden başka bir görüşme açılırsa bant
  // yanlışlıkla taşınmaz).
  const handleCreate = useCallback(async (payload) => {
    const meeting = await createMeeting(payload);
    if (meeting) {
      setSelectedId(meeting.id);
      setCarryOverInfo(meeting.items?.length > 0
        ? { meetingId: meeting.id, count: meeting.items.length }
        : null);
    }
    return meeting;
  }, [createMeeting]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'counterpart',
      label: t('oneOnOnes.fields.person'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row) return null;
          return row.myRole === 'manager' ? row.employeeName : row.managerName;
        },
      },
    },
    {
      name: 'myRole',
      label: t('oneOnOnes.fields.role'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) => {
          const cfg = ONE_ON_ONE_ROLE_COLORS[v] || ONE_ON_ONE_ROLE_COLORS.employee;
          return (
            <Badge color={cfg.color} bg={cfg.bg}>
              {t(`oneOnOnes.role.${v}`, { defaultValue: v })}
            </Badge>
          );
        },
      },
    },
    {
      name: 'status',
      label: t('common.status'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) => {
          const cfg = ONE_ON_ONE_STATUS_COLORS[v] || ONE_ON_ONE_STATUS_COLORS.scheduled;
          return (
            <Badge color={cfg.color} bg={cfg.bg}>
              {t(`oneOnOnes.status.${v}`, { defaultValue: v })}
            </Badge>
          );
        },
      },
    },
    {
      name: 'scheduledAt',
      label: t('oneOnOnes.fields.scheduledAt'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) => formatDateTime(v, lang),
      },
    },
  ], [t, lang, data]);

  return (
    <Container>
      {modalOpen && (
        <NewMeetingModal
          onClose={() => setModalOpen(false)}
          onCreate={handleCreate}
        />
      )}

      {selectedId ? (
        <OneOnOneDetailView
          meeting={selectedMeeting}
          carryOverCount={carryOverInfo?.meetingId === selectedId ? carryOverInfo.count : 0}
          onBack={backToList}
          onUpdateMeeting={updateMeeting}
          onAddItem={addItem}
          onUpdateItem={updateItem}
          onDeleteItem={deleteItem}
        />
      ) : showEmptyManagerHint ? (
        <EmptyManagerCard data-role="empty-manager-hint">
          <EmptyManagerText>{t('oneOnOnes.emptyManager')}</EmptyManagerText>
          <Button onClick={() => setModalOpen(true)}>{t('oneOnOnes.newMeeting')}</Button>
        </EmptyManagerCard>
      ) : (
        <DataTable
          title={t('oneOnOnes.pageTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          serverSide={false}
          canCreateNewRecord={canCreate}
          newButtonLabel={t('oneOnOnes.newMeeting')}
          onNewButtonClick={() => setModalOpen(true)}
          onRowClick={openDetail}
          tableBodyMaxHeight={tableBodyMaxHeight}
        />
      )}
    </Container>
  );
}
